import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function sessionId() {
  let id = localStorage.getItem('zozo_chat_session');
  if (!id) {
    id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('zozo_chat_session', id);
  }
  return id;
}

export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: 'assistant', content: 'Moin! 👋 Ich bin ZOZO, dein Bestell-Assistent. Hunger auf was Bestimmtes – oder soll ich dir was empfehlen?' }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setMsgs(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    try {
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        const snapshot = acc;
        setMsgs(m => [...m.slice(0, -1), { role: 'assistant', content: snapshot }]);
      }
    } catch {
      setMsgs(m => [...m.slice(0, -1), { role: 'assistant', content: 'Ups, da ging was schief. Versuch es gleich nochmal!' }]);
    }
    setBusy(false);
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-4 z-[60] w-[min(92vw,380px)] h-[520px] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden" data-testid="chat-assistant-panel">
          <div className="bg-[#A81C1C] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <p className="font-bold text-sm">ZOZO Assistent 🍔</p>
              <p className="text-[11px] text-red-100">Fragt mich nach Empfehlungen & Angeboten</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="p-1 hover:bg-white/10 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div ref={bodyRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#A81C1C] text-white rounded-br-sm' : 'bg-white border border-stone-200 text-stone-800 rounded-bl-sm'}`}>
                  {m.content || '…'}
                </div>
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-stone-200 bg-white flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder="z. B. Was lohnt sich heute?"
              className="flex-1 px-3 py-2 text-sm border border-stone-300 rounded-full focus:outline-none focus:border-[#A81C1C]"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={busy} className="w-10 h-10 rounded-full bg-[#A81C1C] text-white flex items-center justify-center disabled:opacity-50" data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-4 z-[60] w-14 h-14 rounded-full bg-[#A81C1C] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="chat-assistant-fab"
        aria-label="Bestell-Assistent öffnen"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};
